// ** MUI Components
import {
    Box,
    Typography,
    Tooltip,
    IconButton,
    Avatar,
    AvatarGroup,
  } from "@mui/material";

  // ** Icon Imports
  import Icon from "components/icon";

  // ** Logic Imports
  import { GridActionsCellItem } from "@mui/x-data-grid";
  import { formatUSCurrency } from "components/CurrencyFormat";
  import { convertESTToLocal } from "components/formatDate";
  import { useSettingPermission } from "../permissions/SettingPermissions";

export const GridColumns = ({handleRoleEdit, showConfirmationRoleDelete, handleTeamEdit, showConfirmationTeamDelete, handleSkillEdit, showConfirmationSkillDelete,handleEditTnC, handleDeleteTnC }) => {
    //#region Permissions
    const { SettingPermissionProps } = useSettingPermission();
    const {
      // Role Permissions
      hasRoleSavePermission,
      hasRoleDeletePermission,
  
      // Team Permissions
      hasTeamSavePermission,
      hasTeamDeletePermission,
  
      // Skill Permissions
      hasSkillSavePermission,
      hasSkillDeletePermission,
  
      // TermsAndConditions Permissions
      hasTnCSavePermission,
      hasTnCDeletePermission,
    } = SettingPermissionProps;
    //#endregion Permissions

  const columnsRoleManagement = [
    {
      flex: 0.1,
      minWidth: 150,
      field: "roleName",
      headerName: "Role Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.roleName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 150,
      field: "roleType",
      headerName: "Role Type",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.roleType === "TeamLead"
                ? "Crew Leader"
                : row.roleType === "TeamMember"
                ? "Crew"
                : row.roleType}
            </Typography>
          </Box>
        );
      },
    },
    ...(hasRoleSavePermission || hasRoleDeletePermission
      ? [
          {
            flex: 0.1,
            field: "actions",
            minWidth: 120,
            maxWidth: 120,
            headerName: "Action",
            renderCell: (params) => {
              const { row } = params;
              return (
                <>
                  {hasRoleSavePermission && (
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleRoleEdit(row.id)}>
                        <Icon icon="tabler:pencil" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasRoleDeletePermission && (
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => showConfirmationRoleDelete(row.id)}
                      >
                        <Icon icon="tabler:trash" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              );
            },
          },
        ]
      : []),
  ];

  const columnsTeam = [
    {
      flex: 0.1,
      minWidth: 150,
      field: "crewLeader",
      headerName: "Crew Leader",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.crewLeader}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "teamName",
      headerName: "Team Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                noWrap
                variant="body1"
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    color: "text.primary",
                    textDecoration: "underline",
                  },
                }}
                onClick={() => handleTeamEdit(row.id)}
              >
                {row.teamName}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "createdBy",
      headerName: "Created By",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.createdBy}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "createDate",
      headerName: "Created On",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {convertESTToLocal(row.createDate)}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 180,
      field: "teamMembers",
      headerName: "Team Members",
      renderCell: (params) => {
        const { row } = params;

        const members = row.teamMembers;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <AvatarGroup
              className="pull-up"
              max={3}
              sx={{
                "& .MuiAvatar-root": { width: 25, height: 25 },
                "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
              }}
            >
              {members &&
                members.map((member) => (
                  <Tooltip key={member.employeeID} title={member.employeeName}>
                    <Avatar
                      src="/images/avatars/4.png"
                      alt={member.employeeName}
                    />
                  </Tooltip>
                ))}
            </AvatarGroup>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 120,
      maxWidth: 120,
      headerName: "Action",
      renderCell: (params) => {
        const { row } = params;

        return (
          <>
            <Tooltip title="Edit">
              <IconButton 
                disabled={!hasTeamSavePermission}
                {...(hasTeamSavePermission && {
                  onClick: () => handleTeamEdit(row.id)
                })}
              >
                <Icon icon="tabler:pencil" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Team">
              <IconButton
                color="error"
                disabled={!hasTeamDeletePermission}
                {...(hasTeamDeletePermission && {
                  onClick: () => showConfirmationTeamDelete(row.id)
                })}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const columnsSkillType = [
    {
      flex: 0.1,
      minWidth: 250,
      maxWidth: 300,
      field: "skillName",
      headerName: "Skill Type",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.skillName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "noOfEmployees",
      headerName: "No Of Employees",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {row.noOfEmployees}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "avgHourlyRate",
      headerName: "Average Hourly Rate",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body1">
                {formatUSCurrency(row.avgHourlyRate)}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      type: "actions",
      getActions: ({ id }) => [
        <GridActionsCellItem
          sx={{ transform: "scale(1.5)", marginRight: 3 }}
          icon={<Icon icon="tabler:pencil" />}
          label="Edit"
          disabled={!hasSkillSavePermission}
          {...(hasSkillSavePermission && {
            onClick: () => handleSkillEdit(id)
          })}
        />,
        <GridActionsCellItem
          color="error"
          sx={{ transform: "scale(1.5)" }}
          icon={<Icon icon="tabler:trash" />}
          label="Delete"
          disabled={!hasSkillDeletePermission}
          {...(hasSkillDeletePermission && {
            onClick: () => showConfirmationSkillDelete(id)
          })}
        />,
      ],
    },
  ];

  const columnsSalesTaxRate = [
    {
      flex: 0.1,
      minWidth: 140,
      field: "county",
      headerName: "County",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.county}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "rate",
      headerName: "Rate (%)",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.rate}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 120,
      maxWidth: 120,
      headerName: "Action",
      renderCell: () => (
        <>
          <Tooltip title="Edit">
            <IconButton>
              <Icon icon="tabler:pencil" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton color="error">
              <Icon icon="tabler:trash" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const columnsTermsNConditions = [
    // {
    //   flex: 0.1,
    //   minWidth: 140,
    //   field: "title",
    //   headerName: "Title",
    //   renderCell: (params) => {
    //     const { row } = params;

    //     return (
    //       <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
    //         <Typography noWrap variant="body1">
    //           {row.title}
    //         </Typography>
    //       </Box>
    //     );
    //   },
    // },
    {
      flex: 0.1,
      minWidth: 210,
      field: "Terms",
      headerName: "Terms and Conditions",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.description}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 160,
      field: "moduleName",
      headerName: "Module Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {Array.isArray(row.moduleName)
                ? row.moduleName.join(", ")
                : row.moduleName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "createdOn",
      headerName: "Created On",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.createdOn}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "createdBy",
      headerName: "Created By",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.createdBy}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 160,
      field: "lastUpdated",
      headerName: "Last Updated",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.lastUpdated}
            </Typography>
          </Box>
        );
      },
    },
    // {
    //   flex: 0.1,
    //   minWidth: 120,
    //   field: "version",
    //   headerName: "Version #",
    //   renderCell: (params) => {
    //     const { row } = params;

    //     return (
    //       <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
    //         <Typography noWrap variant="body1">
    //           {row.version}
    //         </Typography>
    //       </Box>
    //     );
    //   },
    // },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 120,
      maxWidth: 120,
      headerName: "Action",
      renderCell: (params) => {
        const { row } = params;
        return (
          <>
            <Tooltip title="Edit">
              <IconButton 
                disabled={!hasTnCSavePermission}
                {...(hasTnCSavePermission && {
                  onClick: () => handleEditTnC(row)
                })}
              >
                <Icon icon="tabler:pencil" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton 
                color="error"
                disabled={!hasTnCDeletePermission}
                {...(hasTnCDeletePermission && {
                  onClick: () => handleDeleteTnC(row)
                })}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  return{
    columnsRoleManagement,
    columnsTeam,
    columnsSkillType,
    columnsSalesTaxRate,
    columnsTermsNConditions,
  }
};
